/**
 * A LIFE — core domain types.
 *
 * This module is deliberately free of any framework import. The whole
 * simulation is expressed in plain data so it can run in a browser, in Node
 * (for the balance lab), or inside a future Android wrapper unchanged.
 */

import type { RngState } from './rng';

/* ------------------------------------------------------------------ *
 * Time & stages
 * ------------------------------------------------------------------ */

/**
 * Life stages are derived from age. The blueprint listed fourteen stages, but
 * several of them ("education transition", "family formation", "reassessment")
 * are *themes* that overlap rather than exclusive periods — a 34-year-old can be
 * simultaneously forming a family and pivoting careers. We therefore keep nine
 * age-derived stages and express the themes through flags and conditions.
 */
export type LifeStage =
  | 'launch'
  | 'earlyCareer'
  | 'establishing'
  | 'growth'
  | 'midlife'
  | 'lateCareer'
  | 'preRetirement'
  | 'retirement'
  | 'lateLife';

export const LIFE_STAGES: readonly LifeStage[] = [
  'launch',
  'earlyCareer',
  'establishing',
  'growth',
  'midlife',
  'lateCareer',
  'preRetirement',
  'retirement',
  'lateLife',
];

/* ------------------------------------------------------------------ *
 * Scalar dimensions
 * ------------------------------------------------------------------ */

export type StatKey = 'health' | 'energy' | 'stress' | 'happiness' | 'purpose';

export const STAT_KEYS: readonly StatKey[] = ['health', 'energy', 'stress', 'happiness', 'purpose'];

export type Stats = Record<StatKey, number>;

export type SkillKey =
  | 'technical'
  | 'business'
  | 'communication'
  | 'management'
  | 'creative'
  | 'social'
  | 'financial'
  | 'selfManagement';

export const SKILL_KEYS: readonly SkillKey[] = [
  'technical',
  'business',
  'communication',
  'management',
  'creative',
  'social',
  'financial',
  'selfManagement',
];

export type Skills = Record<SkillKey, number>;

export type TraitKey =
  | 'ambition'
  | 'discipline'
  | 'patience'
  | 'riskTolerance'
  | 'optimism'
  | 'socialConfidence'
  | 'resilience'
  | 'adaptability'
  | 'impulsiveness'
  | 'conscientiousness'
  | 'curiosity'
  | 'empathy'
  | 'conflictAvoidance'
  | 'noveltySeeking';

export const TRAIT_KEYS: readonly TraitKey[] = [
  'ambition',
  'discipline',
  'patience',
  'riskTolerance',
  'optimism',
  'socialConfidence',
  'resilience',
  'adaptability',
  'impulsiveness',
  'conscientiousness',
  'curiosity',
  'empathy',
  'conflictAvoidance',
  'noveltySeeking',
];

export type Traits = Record<TraitKey, number>;

export type HabitKey =
  | 'exercise'
  | 'sleep'
  | 'saving'
  | 'investing'
  | 'learning'
  | 'workaholism'
  | 'socialising'
  | 'procrastination'
  | 'spending'
  | 'relationshipCare';

export const HABIT_KEYS: readonly HabitKey[] = [
  'exercise',
  'sleep',
  'saving',
  'investing',
  'learning',
  'workaholism',
  'socialising',
  'procrastination',
  'spending',
  'relationshipCare',
];

export type Habits = Record<HabitKey, number>;

/* ------------------------------------------------------------------ *
 * Finance
 * ------------------------------------------------------------------ */

export type DebtKind = 'student' | 'consumer' | 'mortgage' | 'business' | 'medical';

export interface Debt {
  id: string;
  kind: DebtKind;
  label: string;
  principal: number;
  /** Annual nominal rate, e.g. 0.06 */
  rate: number;
  /** Annual minimum payment. */
  minPayment: number;
  originAge: number;
}

export type AssetClassId = 'cash' | 'conservative' | 'balanced' | 'growth' | 'speculative' | 'property';

export interface Holding {
  assetId: AssetClassId;
  /** Current market value. */
  value: number;
  /** Total nominal amount ever contributed, for reporting gains. */
  contributed: number;
}

export interface PropertyState {
  owned: boolean;
  value: number;
  mortgageDebtId: string | null;
  purchaseAge: number;
}

export interface Finance {
  cash: number;
  /** Annual gross salary from employment. 0 when not employed. */
  salary: number;
  /** Other recurring annual income (freelance, royalties, pension). */
  otherIncome: number;
  /** Annual committed costs: housing, insurance, debt-independent fixed bills. */
  fixedExpenses: number;
  /**
   * Discretionary spending tier 0–10. Higher tiers cost more but feed happiness
   * with diminishing returns, which is how lifestyle inflation is modelled.
   */
  lifestyleLevel: number;
  debts: Debt[];
  holdings: Record<AssetClassId, Holding>;
  retirementAssets: number;
  property: PropertyState;
  /** Player-set aspiration for the emergency fund, in months of expenses. */
  emergencyMonthsTarget: number;
  /** Rolling record for the end-of-life report. */
  lifetimeEarnings: number;
  lifetimeSpending: number;
  peakNetWorth: number;
  bankruptcies: number;
}

/* ------------------------------------------------------------------ *
 * Career & education
 * ------------------------------------------------------------------ */

export type CareerStatus =
  | 'none'
  | 'student'
  | 'employed'
  | 'selfEmployed'
  | 'founder'
  | 'break'
  | 'unemployed'
  | 'retired';

export interface Career {
  status: CareerStatus;
  fieldId: string | null;
  /** 0-based index into the field's role ladder. */
  roleLevel: number;
  /** Years in the current role. */
  tenure: number;
  /** 0–100 hours-pressure abstraction. */
  workload: number;
  /** 0–100 how well the player is performing. */
  performance: number;
  /** 0–100 likelihood the job survives a downturn. */
  jobSecurity: number;
  /** 0–100 how aligned the work feels with the player's values. */
  meaningAlignment: number;
  /** 0–100 professional standing within the field. */
  reputation: number;
  yearsExperience: number;
  breaksTaken: number;
  pivots: number;
  layoffs: number;
  promotions: number;
  /** Years since the player last actively pushed for advancement. */
  yearsSinceAdvance: number;
}

export type EducationLevel = 'highschool' | 'vocational' | 'bachelor' | 'master' | 'doctorate';

export const EDUCATION_RANK: Record<EducationLevel, number> = {
  highschool: 0,
  vocational: 1,
  bachelor: 2,
  master: 3,
  doctorate: 4,
};

export interface Enrollment {
  programId: string;
  yearsRemaining: number;
  /** Annual cost while enrolled. */
  annualCost: number;
  /** Fraction of normal working capacity retained (0 = full-time study). */
  workCapacity: number;
}

export interface Education {
  level: EducationLevel;
  credentials: string[];
  enrollment: Enrollment | null;
  /** Total years spent in formal education after age 18. */
  yearsStudied: number;
  /** Total money spent on education. */
  totalCost: number;
}

/* ------------------------------------------------------------------ *
 * Relationships & family
 * ------------------------------------------------------------------ */

export type PersonKind =
  | 'parent'
  | 'sibling'
  | 'friend'
  | 'mentor'
  | 'partner'
  | 'spouse'
  | 'child'
  | 'colleague'
  | 'rival'
  | 'ex';

export interface Bond {
  /** 0–100 confidence that this person acts in the player's interest. */
  trust: number;
  /** 0–100 emotional proximity; decays with neglect. */
  closeness: number;
  /** 0–100 structural fit of values and lifestyle. Mostly fixed per person. */
  compatibility: number;
  /** 0–100 practical and emotional help available. */
  support: number;
  /** 0–100 unresolved friction. */
  conflict: number;
}

export interface Person {
  id: string;
  name: string;
  kind: PersonKind;
  age: number;
  alive: boolean;
  bond: Bond;
  /** Player age at the most recent meaningful interaction. */
  lastInteractionAge: number;
  tags: string[];
  /** For children: schooling stage, dependence, etc. */
  meta: Record<string, number | string | boolean>;
}

export type MaritalStatus = 'single' | 'dating' | 'partnered' | 'married' | 'separated' | 'divorced' | 'widowed';

export interface Family {
  maritalStatus: MaritalStatus;
  /** Player age when the current union began. */
  unionSinceAge: number | null;
  marriages: number;
  divorces: number;
  childrenBorn: number;
  /** Household members who depend financially on the player. */
  dependents: number;
  /** 0–100 quality of the household as an environment. */
  householdHarmony: number;
}

/* ------------------------------------------------------------------ *
 * Business
 * ------------------------------------------------------------------ */

export interface Business {
  active: boolean;
  archetypeId: string | null;
  name: string;
  /** Money invested and retained inside the business. */
  capital: number;
  /** Annual revenue. */
  revenue: number;
  /** Annual operating cost. */
  operatingCost: number;
  /** Trailing growth multiplier applied to revenue, e.g. 0.12 = +12%. */
  growth: number;
  /** 0–100 fragility. */
  risk: number;
  /** 0–100 operational and financial health. */
  health: number;
  /** 0–100 how much of the owner's week it consumes. */
  timeDemand: number;
  /** Market value if sold today. */
  valuation: number;
  yearsRunning: number;
  /** Non-null once exited: the sale proceeds. */
  exitProceeds: number | null;
  failures: number;
  exits: number;
}

/* ------------------------------------------------------------------ *
 * Goals, dreams, purpose
 * ------------------------------------------------------------------ */

export type AspirationKind = 'goal' | 'dream';
export type AspirationStatus = 'active' | 'achieved' | 'abandoned' | 'failed';

export interface Aspiration {
  id: string;
  defId: string;
  kind: AspirationKind;
  status: AspirationStatus;
  /** 0–100. */
  progress: number;
  adoptedAge: number;
  resolvedAge: number | null;
  /** Soft deadline; missing it converts to regret rather than failure. */
  targetAge: number | null;
}

/* ------------------------------------------------------------------ *
 * Inertia (procrastination)
 * ------------------------------------------------------------------ */

export type InertiaTopic =
  | 'career'
  | 'health'
  | 'finance'
  | 'relationship'
  | 'education'
  | 'business'
  | 'leisure'
  | 'family';

/**
 * An intention the player has formed but not executed. Every year it survives,
 * it costs a little (opportunity cost) and becomes slightly harder to act on.
 */
export interface Intention {
  id: string;
  label: string;
  topic: InertiaTopic;
  createdAge: number;
  deferrals: number;
  /** 0–100 how costly continued delay is. */
  urgency: number;
  /** Event to fire if the intention finally decays away. */
  decayEventId: string | null;
  /** Event to re-offer when the player revisits the intention. */
  resumeEventId: string | null;
}

export interface Inertia {
  /** 0–100 general resistance to starting things. */
  pressure: number;
  intentions: Intention[];
  /** Lifetime counters used by the ending report. */
  actedCount: number;
  deferredCount: number;
  decayedCount: number;
}

/* ------------------------------------------------------------------ *
 * Burnout
 * ------------------------------------------------------------------ */

export type BurnoutStage =
  | 'healthy'
  | 'overextended'
  | 'exhausted'
  | 'atRisk'
  | 'burntOut'
  | 'recovering'
  | 'rebuilding';

export interface BurnoutState {
  stage: BurnoutStage;
  /** 0–100 accumulated allostatic load. */
  load: number;
  yearsInStage: number;
  episodes: number;
  /** Player age at the last full burnout. */
  lastEpisodeAge: number | null;
}

/* ------------------------------------------------------------------ *
 * History, memory, legacy
 * ------------------------------------------------------------------ */

export type LogTone = 'positive' | 'negative' | 'neutral' | 'milestone';

export type LogCategory =
  | 'life'
  | 'career'
  | 'education'
  | 'finance'
  | 'investment'
  | 'business'
  | 'relationship'
  | 'family'
  | 'health'
  | 'wellbeing'
  | 'goal'
  | 'crisis'
  | 'luck'
  | 'inertia'
  | 'legacy'
  | 'system';

export interface LogEntry {
  age: number;
  category: LogCategory;
  tone: LogTone;
  title: string;
  detail?: string;
  /** Marks entries worth surfacing in the end-of-life timeline. */
  weight: number;
  tags: string[];
}

export interface DecisionRecord {
  age: number;
  eventId: string;
  eventTitle: string;
  choiceId: string;
  choiceLabel: string;
  outcomeLabel?: string;
  /** Tags copied from the chosen option, used by archetype scoring. */
  tags: string[];
}

export interface Regret {
  id: string;
  age: number;
  label: string;
  /** 0–100 how much this still weighs. Decays slowly, or is resolved. */
  weight: number;
  resolved: boolean;
}

export interface Achievement {
  id: string;
  age: number;
  label: string;
  category: LogCategory;
}

/* ------------------------------------------------------------------ *
 * Delayed consequences
 * ------------------------------------------------------------------ */

export interface ScheduledEffect {
  id: string;
  dueAge: number;
  /** Human-readable trace back to the originating decision. */
  sourceLabel: string;
  sourceEventId: string | null;
  sourceAge: number;
  effects: Effect[];
  /** Optional event to trigger instead of / in addition to raw effects. */
  eventId: string | null;
  /** Conditions that must still hold, else the scheduled effect is dropped. */
  requires?: Condition[];
  narrative?: string;
}

/* ------------------------------------------------------------------ *
 * Conditions (data-driven predicates)
 * ------------------------------------------------------------------ */

export type CompareOp = '>' | '>=' | '<' | '<=' | '==' | '!=';

export type Condition =
  /** Numeric comparison against a value in the flattened query view. */
  | { k: 'num'; path: string; op: CompareOp; value: number }
  /** String comparison against the query view. */
  | { k: 'str'; path: string; op: '==' | '!='; value: string }
  | { k: 'stage'; anyOf: LifeStage[] }
  | { k: 'age'; min?: number; max?: number }
  | { k: 'flag'; flag: string; value?: boolean }
  | { k: 'marital'; anyOf: MaritalStatus[] }
  | { k: 'careerStatus'; anyOf: CareerStatus[] }
  | { k: 'hasPerson'; kind: PersonKind; min?: number }
  | { k: 'hasCredential'; id: string }
  | { k: 'educationAtLeast'; level: EducationLevel }
  | { k: 'skill'; skill: SkillKey; op: CompareOp; value: number }
  | { k: 'trait'; trait: TraitKey; op: CompareOp; value: number }
  | { k: 'habit'; habit: HabitKey; op: CompareOp; value: number }
  | { k: 'burnout'; anyOf: BurnoutStage[] }
  | { k: 'eventSeen'; id: string; value?: boolean }
  | { k: 'intention'; topic?: InertiaTopic; id?: string; present: boolean }
  | { k: 'aspiration'; defId: string; status?: AspirationStatus; present?: boolean }
  | { k: 'not'; of: Condition }
  | { k: 'all'; of: Condition[] }
  | { k: 'any'; of: Condition[] };

/* ------------------------------------------------------------------ *
 * Effects (data-driven state mutations)
 * ------------------------------------------------------------------ */

export type NumericMode = 'add' | 'mult' | 'set';

export type Effect =
  | { k: 'stat'; stat: StatKey; delta: number }
  | { k: 'statSet'; stat: StatKey; value: number }
  | { k: 'cash'; amount: number; note?: string }
  /** Cash amount expressed as a multiple of current annual gross income. */
  | { k: 'cashIncomeMonths'; months: number; note?: string }
  | { k: 'salary'; mode: NumericMode; value: number }
  | { k: 'otherIncome'; mode: NumericMode; value: number }
  | { k: 'fixedExpenses'; mode: NumericMode; value: number }
  | { k: 'lifestyle'; delta: number }
  | { k: 'debt'; op: 'add'; kind: DebtKind; label: string; amount: number; rate: number; years: number }
  | { k: 'debt'; op: 'pay'; amount: number; kind?: DebtKind }
  | { k: 'debt'; op: 'forgive'; kind?: DebtKind; fraction?: number }
  | { k: 'invest'; op: 'buy' | 'sell'; assetId: AssetClassId; amount: number }
  | { k: 'invest'; op: 'shock'; assetId: AssetClassId; fraction: number }
  | { k: 'retirement'; mode: NumericMode; value: number }
  | { k: 'property'; op: 'buy'; value: number; deposit: number; rate: number; years: number }
  | { k: 'property'; op: 'sell' }
  | { k: 'skill'; skill: SkillKey; delta: number }
  | { k: 'trait'; trait: TraitKey; delta: number }
  | { k: 'habit'; habit: HabitKey; delta: number }
  | { k: 'career'; op: 'setStatus'; status: CareerStatus }
  | { k: 'career'; op: 'setField'; fieldId: string; roleLevel?: number }
  | { k: 'career'; op: 'promote'; levels?: number }
  | { k: 'career'; op: 'demote'; levels?: number }
  | { k: 'career'; op: 'layoff' }
  | { k: 'career'; op: 'quit' }
  | { k: 'career'; op: 'retire' }
  | { k: 'career'; op: 'adjust'; field: 'workload' | 'performance' | 'jobSecurity' | 'meaningAlignment' | 'reputation'; delta: number }
  | { k: 'education'; op: 'enroll'; programId: string }
  | { k: 'education'; op: 'dropOut' }
  | { k: 'education'; op: 'grantCredential'; id: string; level?: EducationLevel }
  | { k: 'relationship'; who: PersonRef; dim: keyof Bond; delta: number }
  | { k: 'relationshipAll'; kind?: PersonKind; dim: keyof Bond; delta: number }
  | { k: 'person'; op: 'add'; kind: PersonKind; tags?: string[]; nameHint?: string; ageOffset?: number; bond?: Partial<Bond> }
  | { k: 'person'; op: 'remove'; who: PersonRef; reason?: string }
  | { k: 'person'; op: 'convert'; who: PersonRef; to: PersonKind }
  | { k: 'person'; op: 'interact'; who: PersonRef }
  | { k: 'marital'; status: MaritalStatus }
  | { k: 'family'; op: 'childBorn' }
  | { k: 'family'; op: 'adjustHarmony'; delta: number }
  | { k: 'business'; op: 'start'; archetypeId: string; capital: number }
  | { k: 'business'; op: 'invest'; amount: number }
  | { k: 'business'; op: 'adjust'; field: 'growth' | 'risk' | 'health' | 'timeDemand'; delta: number }
  | { k: 'business'; op: 'fail' }
  | { k: 'business'; op: 'exit'; multiple?: number }
  | { k: 'burnout'; op: 'load'; delta: number }
  | { k: 'burnout'; op: 'setStage'; stage: BurnoutStage }
  | { k: 'flag'; flag: string; value: boolean }
  | { k: 'counter'; name: string; delta: number }
  | { k: 'aspiration'; op: 'adopt'; defId: string }
  | { k: 'aspiration'; op: 'progress'; defId: string; delta: number }
  | { k: 'aspiration'; op: 'resolve'; defId: string; status: AspirationStatus }
  | { k: 'intention'; op: 'add'; id: string; label: string; topic: InertiaTopic; urgency?: number; decayEventId?: string; resumeEventId?: string }
  | { k: 'intention'; op: 'resolve'; id: string }
  | { k: 'intention'; op: 'defer'; id: string }
  | { k: 'inertia'; op: 'pressure'; delta: number }
  | { k: 'regret'; op: 'add'; id: string; label: string; weight: number }
  | { k: 'regret'; op: 'resolve'; id: string }
  | { k: 'achievement'; id: string; label: string; category: LogCategory }
  | { k: 'memory'; title: string; detail?: string; tone: LogTone; category: LogCategory; weight?: number }
  | { k: 'schedule'; afterYears: number; label: string; effects: Effect[]; eventId?: string; requires?: Condition[]; narrative?: string }
  | { k: 'triggerEvent'; eventId: string; afterYears?: number }
  | { k: 'health'; op: 'condition'; id: string; label: string; severity: number }
  | { k: 'health'; op: 'resolveCondition'; id: string }
  | { k: 'end'; cause: string };

/** How an effect target refers to a person. */
export type PersonRef =
  | { r: 'id'; id: string }
  | { r: 'kind'; kind: PersonKind; pick?: 'first' | 'closest' | 'weakest' | 'random' }
  | { r: 'spouse' }
  | { r: 'partner' }
  | { r: 'subject' };

/* ------------------------------------------------------------------ *
 * Health conditions
 * ------------------------------------------------------------------ */

export interface HealthCondition {
  id: string;
  label: string;
  /** 0–100; drives ongoing cost and stat drag. */
  severity: number;
  onsetAge: number;
  chronic: boolean;
}

/* ------------------------------------------------------------------ *
 * Event bookkeeping
 * ------------------------------------------------------------------ */

export interface EventMemory {
  /** Player age at last occurrence, per event id. */
  lastSeenAge: Record<string, number>;
  /** Times each event has fired. */
  counts: Record<string, number>;
}

/* ------------------------------------------------------------------ *
 * Board
 * ------------------------------------------------------------------ */

export type NodeKind =
  | 'start'
  | 'milestone'
  | 'education'
  | 'career'
  | 'relationship'
  | 'family'
  | 'finance'
  | 'investment'
  | 'business'
  | 'leisure'
  | 'health'
  | 'event'
  | 'opportunity'
  | 'risk'
  | 'recovery'
  | 'crossroads'
  | 'retirement'
  | 'legacy'
  | 'end';

export interface BoardNode {
  id: string;
  kind: NodeKind;
  title: string;
  /** Short flavour line shown on hover / focus. */
  subtitle?: string;
  /** Horizontal index; nodes in the same column are parallel routes. */
  column: number;
  /** Vertical lane, 0 = centre; negative = above, positive = below. */
  lane: number;
  stage: LifeStage;
  /** Years consumed by stepping onto this node. */
  yearCost: number;
  next: string[];
  /** Event pool tags used to draw an event when the player lands here. */
  pools: string[];
  /** Action menu identifier, if this node opens a system screen. */
  actions: string[];
  milestone: boolean;
}

export interface BoardModel {
  id: string;
  nodes: Record<string, BoardNode>;
  order: string[];
  startId: string;
  endId: string;
  columns: number;
}

/* ------------------------------------------------------------------ *
 * Player state
 * ------------------------------------------------------------------ */

export interface Identity {
  name: string;
  pronoun: 'they' | 'she' | 'he';
  originId: string;
  /** Free-form flavour chosen at setup. */
  motivationId: string;
}

export interface PlayerState {
  /* identity & bookkeeping */
  saveVersion: number;
  gameVersion: string;
  contentVersion: string;
  lifeId: string;
  runId: string;
  rng: RngState;
  createdAt: number;
  updatedAt: number;

  identity: Identity;
  age: number;
  startAge: number;
  stage: LifeStage;
  alive: boolean;
  causeOfDeath: string | null;
  /** Hidden, re-rolled each year: soft life expectancy pressure. */
  frailty: number;

  stats: Stats;
  skills: Skills;
  traits: Traits;
  habits: Habits;

  finance: Finance;
  career: Career;
  education: Education;
  people: Person[];
  family: Family;
  business: Business;
  burnout: BurnoutState;
  inertia: Inertia;
  conditions: HealthCondition[];
  aspirations: Aspiration[];

  flags: Record<string, boolean>;
  counters: Record<string, number>;

  log: LogEntry[];
  decisions: DecisionRecord[];
  regrets: Regret[];
  achievements: Achievement[];
  scheduled: ScheduledEffect[];
  eventMemory: EventMemory;

  /** Board navigation. */
  board: BoardModel;
  nodeId: string;
  visitedNodes: string[];

  /** Queue of event ids that must fire before free events are drawn. */
  pendingEvents: string[];

  /** Market regime, shared by all risk assets. */
  market: {
    regime: 'boom' | 'normal' | 'soft' | 'bust';
    yearsInRegime: number;
  };

  /** End-of-life summary, filled once. */
  ending: EndingReport | null;
}

/* ------------------------------------------------------------------ *
 * Ending
 * ------------------------------------------------------------------ */

export interface EndingDimension {
  key: string;
  label: string;
  /** 0–100 */
  score: number;
  note: string;
}

export interface EndingReport {
  age: number;
  cause: string;
  archetypeId: string;
  archetypeLabel: string;
  archetypeNarrative: string;
  dimensions: EndingDimension[];
  timeline: LogEntry[];
  headline: string;
  netWorth: number;
  legacyScore: number;
  epitaph: string;
  stats: Stats;
  highlights: string[];
  setbacks: string[];
  unfinished: string[];
}

/* ------------------------------------------------------------------ *
 * Runtime turn output
 * ------------------------------------------------------------------ */

export interface StatDelta {
  key: string;
  label: string;
  before: number;
  after: number;
}

/** Everything that happened during one engine step, for the UI to animate. */
export interface TurnReport {
  yearsAdvanced: number;
  entries: LogEntry[];
  deltas: StatDelta[];
  ended: boolean;
}
